import React, { Component } from "react";
import { Col } from "react-bootstrap";
import { UserStatus } from "constants/UserConstants";
import MaterialTable from "material-table";
import { tableIcons } from "utils/MaterialTableHelper";
import "./SupervisorLiveStats.scss";
import { uepClient } from "uepClient";
import moment from "moment";
import SupervisorRefreshService from "services/SupervisorRefreshService";
import colors from "styles/colors.module.scss";
import WithAlert from "hoc/WithAlert";
import { getBusinessUnit, getGroup } from "utils/Common";
import { UserGroup } from "constants/UserConstants";
import Channels from "components/Channels/Channels";

class SupervisorLiveStats extends Component {
  constructor() {
    super();
    this.state = {
      isLoading: true,
      agentStatsData: [],
    };
    this.agentBusinessUnit = getBusinessUnit();
    this.agentGroup = getGroup();
  }

  componentDidMount() {
    this.refreshService = SupervisorRefreshService.listen().subscribe(
      this.onRefreshFired
    );
    this.fetchAndSetAgentStatsData();
  }

  componentWillUnmount() {
    this.refreshService.unsubscribe();
  }

  onRefreshFired = () => {
    this.fetchAndSetAgentStatsData();
  };

  fetchAndSetAgentStatsData = () => {
    this.setState({
      isLoading: true,
    });
    uepClient()
      .get(`/stats/loggedin-agents?group=${this.agentGroup}`)
      .then((res) => {
        const statsData = res.data;
        const agentStatsData = statsData.agentStats.map((stat) => ({
          agentId: stat.agentId,
          name: `${stat.firstName} ${stat.lastName}`,
          activeSessionsCount: stat.activeSessionsCount,
          skills: stat.skills,
          group: stat.group,
          status: UserStatus[stat.status],
          lastStatusChangeTime: stat.lastStatusChangeTime,
          channels: stat.channels,
        }));
        this.setState({
          agentStatsData,
          isLoading: false,
        });
      })
      .catch((err) => {
        this.props.showAlert({
          variant: "danger",
          content: (
            <span>
              <b>Error!</b> Failed to fetch the data for logged in agents.
            </span>
          ),
        });
        console.error(err.message);
      });
  };

  render() {
    const { isLoading } = this.state;
    return (
      <div className="supervisor-live-stats">
        <div className="supervisor-agent-stats">
          <Col xs="4" className="active-stat-column">
            {this.renderAgentStatCard({
              stat: "Agents in Chat",
              count: this.getCountOfAgents(UserStatus.ACTIVE),
              className: "agents-active",
            })}
          </Col>
          <Col xs="4" className="active-stat-column">
            {this.renderAgentStatCard({
              stat: "Agents Ready",
              count: this.getCountOfAgents(UserStatus.READY),
              className: "agents-ready",
            })}
          </Col>
          <Col xs="4" className="active-stat-column">
            {this.renderAgentStatCard({
              stat: "Agents Inactive",
              count: this.getCountOfAgents(UserStatus.INACTIVE),
              className: "agents-inactive",
            })}
          </Col>
        </div>
        <div className="supervisor-live-stats-table-container">
          <div className="table-wrapper">
            <MaterialTable
              localization={{
                body: { emptyDataSourceMessage: <p>No agents logged in.</p> },
              }}
              icons={tableIcons}
              title="Team View"
              columns={this.getLiveTableColumns()}
              data={this.getLiveTableRecords()}
              options={{
                search: false,
                paging: false,
                sorting: false,
                draggable: false,
              }}
              components={{
                Toolbar: () => {
                  return (
                    <div className="table-header">
                      <div className="table-title">Team View</div>
                      {this.agentGroup !== UserGroup.SERVICE && (
                        <div className="stat-msg">
                          {"** Max Session per Agent "}
                          <span className="session-count">{"03 no."}</span>
                        </div>
                      )}
                    </div>
                  );
                },
              }}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    );
  }
  renderStatusValue = (status) => {
    switch (status) {
      case UserStatus.LOGIN:
        return "Not Ready";
      case UserStatus.READY:
        return "Ready";
      case UserStatus.NOT_READY:
        return "Not Ready";
      case UserStatus.BIOBREAK:
        return "Bio-break";
      case UserStatus.LUNCHBREAK:
        return "Lunch-break";
      case UserStatus.TRAINING:
        return "Training";
      default:
        return "";
    }
  };
  getLiveTableColumns = () => {
    const tableColumns = [
      {
        title: "Name",
        field: "name",
      },
      {
        title: "Status",
        field: "status",
        render: (rowData) => {
          return this.renderStatusValue(rowData.status);
        },
      },
      {
        title:
          this.agentGroup !== UserGroup.SERVICE
            ? "Active Session**"
            : "Active Session",
        field: "activeSessionsCount",
      },
      {
        title: "Skill",
        field: "skill",
        render: (rowData) => {
          if (rowData.skills.length) {
            const skillNames = rowData.skills.map((skill) => skill.name);
            return skillNames.join(", ");
          } else return "";
        },
      },
      {
        title: "Group",
        field: "group",
      },
      {
        title: "Time",
        field: "lastStatusChangeTime",
        render: (rowData) => {
          return moment
            .utc(rowData.lastStatusChangeTime)
            .local()
            .format("DD MMM YYYY hh:mm a");
        },
      },
      {
        title: "Channel",
        field: "channel",
        render: (rowData) => {
          if (rowData.channels.length) {
            return <Channels channels={rowData.channels} />;
          } else return "";
        },
      },
    ];

    const tableColumnsWithStyle = tableColumns.map((tc) => ({
      ...tc,
      cellStyle: {
        border: 0,
        color: colors.darkGrey,
        fontWeight: "normal",
      },
      headerStyle: {
        border: 0,
        color: colors.darkGrey,
        fontWeight: "300",
        fontSize: "1em",
      },
    }));
    return tableColumnsWithStyle;
  };
  getLiveTableRecords = () => {
    return this.state.agentStatsData;
  };
  getCountOfAgents = (status) => {
    const { agentStatsData } = this.state;
    switch (status) {
      case UserStatus.ACTIVE:
        return agentStatsData.filter((s) => s.activeSessionsCount > 0).length;
      case UserStatus.INACTIVE:
        return agentStatsData.filter(
          (s) =>
            s.status === UserStatus.NOT_READY ||
            s.status === UserStatus.INACTIVE ||
            s.status === UserStatus.LOGIN ||
            s.status === UserStatus.BIOBREAK ||
            s.status === UserStatus.LUNCHBREAK ||
            s.status === UserStatus.TRAINING
        ).length;
      case UserStatus.READY:
        return agentStatsData.filter((s) => s.status === UserStatus.READY)
          .length;
      default:
        return 0;
    }
  };
  renderAgentStatCard = ({ stat, count, className = "" }) => {
    return (
      <div className={`agent-stat-card ${className}`}>
        <div className="stat">{stat}</div>
        <h2 className="count">{count}</h2>
      </div>
    );
  };
}

export default WithAlert(SupervisorLiveStats);
